import React, { useState, useEffect } from 'react';
import GenericIcon from 'components/GenericIcon';
import UpArrow from "product/application/icon-up-arrow.svg";
import DownArrow from "product/application/icon-down-arrow.svg";

const interestMatrix = I18n.t(`calculators.interest_matrix`);

const Card = ({
  cardHeaderText,
  lvr,
  theme,
  font,
  applyNow,
  talkToExpert,
  monthlyPayment,
  loanObjective,
  repaymentType,
  setSpecialTypeRate,
  setRegularTypeRate,
  isOwnHome,
  isInterestOnly,
  applyButton,
  displayFourCards,
  setPoorTypeRate,
  setLeaseDocTypeRate,
  setSmsfResidentRate,
}) => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 991);
  const [collapse, setCollapse] = useState(true);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 991);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const listGroupMarginBottom = isDesktop ? 'mb-5' : 'mb-3';
  const listGroup = `list-group ${theme} ${listGroupMarginBottom} ${displayFourCards}`;
  const listGroupItem = `list-group-item ${theme} ${displayFourCards}`;
  const collapseText = collapse ? 'More details' : 'Less details';
  const collapseTextMargin = collapse ? 'mt-5' : 'mt-2';
  const Arrow = collapse ? <DownArrow /> : <UpArrow />;
  const column = displayFourCards === 'display-four-cards' ? 'col-md-3' : 'col-md-4';

  let lvrType = 'regular';
  let goodFor = ['Low rate and low fees', 'Purchase and refinance', 'Debt consolidation and equity release'];
  let features = ['LVR up to 80%', 'Additional repayments', 'Free redraw', 'Split accounts', 'Loan top ups available'];

  let interestRate = interestMatrix[loanObjective]?.[lvrType]?.variable[repaymentType] || '-';

  const updateRatesAndFeatures = () => {
    switch (lvr) {
      case '60':
        setSpecialTypeRate(interestMatrix[loanObjective][lvrType].payg_variable[repaymentType]);
        interestRate = interestMatrix[loanObjective][lvrType].payg_variable;
        if (!isInterestOnly) {
           features = ['LVR up to 60%', 'Additional repayments', 'Free redraw', 'Split accounts', 'Loan top ups available'];
        }
        if (!isOwnHome && !isInterestOnly) {
           features = ['LVR up to 80%', 'Additional repayments', 'Free redraw', 'Split accounts', 'Loan top ups available'];
        }
        break;
      case '80':
        lvrType = 'regular';
        setRegularTypeRate(interestMatrix[loanObjective][lvrType].variable[repaymentType]);
        interestRate = interestMatrix[loanObjective][lvrType].variable;
        if (isInterestOnly) {
          goodFor = ['Purchase and refinance', 'Debt consolidation and equity release', 'No annual reviews'];
          features = ['LVR up to 65%', 'P&I or Interest only', 'Additional repayments', '3-30 year loan terms', 'Loan amounts to $3m'];
        } 
        if (!isOwnHome && !isInterestOnly) {
          goodFor = ['Offset facility', 'Purchase and refinance', 'Debt consolidation and equity release'];
          features = ['LVR up to 80%', 'Up to 10 offset accounts per loan account', 'Additional repayments', 'Free redraw', 'Split accounts'];
        }
        if (isOwnHome && !isInterestOnly) {
          goodFor = ['Offset facility', 'Purchase and refinance', 'Debt consolidation and equity release'];
          features = ['LVR up to 60%', 'Up to 10 offset accounts per loan account', 'Additional repayments', 'Free redraw', 'Split accounts'];
        }
        break;
      case 'lease_doc':
        setLeaseDocTypeRate(interestMatrix[loanObjective][lvr].variable[repaymentType]);
        interestRate = interestMatrix[loanObjective][lvr].variable;
        goodFor = isInterestOnly
          ? ['Purchase and refinance', 'Debt consolidation and equity release', 'No annual reviews']
          : ['Self employed', 'Flexible income verification', 'Purchase and refinance'];
        if (isInterestOnly) {
          features = ['LVR up to 65%', 'P&I or Interest only', 'Additional repayments', '3-30 year loan terms', 'Loan amounts to $3m'];
        } 
        break;
      case '90':
        lvrType = 'poor';
        setPoorTypeRate(interestMatrix[loanObjective][lvrType].variable[repaymentType]);
        interestRate = interestMatrix[loanObjective][lvrType].variable;
        goodFor = isInterestOnly
          ? ['Flexible income verification', 'Purchase and refinance', 'Debt consolidation and equity release', 'No annual reviews']
          : ['Self employed', 'Flexible income verification', 'Purchase and refinance', 'Debt consolidation and equity release'];
        if (!isOwnHome && !isInterestOnly) {
           goodFor = ['Self-employed', 'Flexible income verification', 'Purchase and refinance', 'Debt consolidation and equity release'];
           features = ['LVR up to 65%', 'Additional repayments', 'Free redraw', 'Split accounts', 'Optional offset facility for +0.10% p.a.^'];
        }
        // Live in Residential low doc home loan
        if (isOwnHome && !isInterestOnly) {
           features = ['LVR up to 65%', 'Additional repayments', 'Free redraw', 'Split accounts', 'Optional offset facility for +0.10% p.a.^'];
        }
        if (isInterestOnly) {
          features = ['LVR up to 65%', 'P&I or Interest only', 'Additional repayments', '3-30 year loan terms', 'Loan amounts to $3m'];
        } 
        break;
      case 'smsf':
        setSmsfResidentRate(interestMatrix[loanObjective][lvr].variable[0]);
        interestRate = interestMatrix[loanObjective][lvr].variable;
        goodFor = ['SMSF borrower', 'Refinance', 'Offset facility'];
        features = ['LVR up to 70%', 'Additional repayments', 'Optional offset facility', 'Split accounts'];
        break;
      case 'smsf_commercial':
        setSpecialTypeRate(interestMatrix[loanObjective][lvr].variable[0]);
        interestRate = interestMatrix[loanObjective][lvr].variable;
        goodFor = ['SMSF borrower', 'Purchase and refinance', 'Offset facility'];
        features = ['LVR up to 70%', 'Additional repayments', 'Optional offset facility', 'Split accounts'];
        break;
      default:
        break;
    }
  };

  updateRatesAndFeatures();

  const renderListItems = (items) => items.map((item, index) => (
    <li key={index} className={listGroupItem}>
      <img src={gon.assets.iconCheckCircle} alt="Check icon" /> {item}
    </li>
  ));

  const rate =  lvr.includes('smsf') ? '0' : repaymentType

  if (interestRate[repaymentType] === '-') return null;

  return (
    <div className={`${column} card-special`}>
      <div className={`card ${theme} ${displayFourCards}`}>
        <div className={`card-header card-top ${theme}`} />
        <div className={`card-body pb-1 ${displayFourCards}`}>
          <div className={`d-flex justify-content-evenly card-header-text-container ${displayFourCards}`}>
            <div className={`bigger ${font}`}>{cardHeaderText}</div>
          </div>
          <div className='d-flex rate-bottom-border'>
            <div className={`rate-percentage ${font}`}>
              <span className={`rate-percentage__number ${font} ${displayFourCards}`}>
                {interestRate[rate].toFixed(2)}
              </span>
              <span className={`rate-percentage__percentage ${font} ${displayFourCards}`}>%</span>
              p.a.
              <div className={font}>interest rate</div>
            </div>
            {!isInterestOnly && (
              <div className={`rate-percentage text-right ${font}`}>
                <span className={`rate-percentage__number ${font} ${displayFourCards}`}>
                  {interestRate[1].toFixed(2)}
                </span>
                <span className={`rate-percentage__percentage ${font} ${displayFourCards}`}>%</span>
                p.a.
                <div className={`${font} comparison-rate ${displayFourCards}`}>comparison rate</div>
              </div>
            )}
          </div>
          <div className='repayment'>
            <div className={`mt-3 mb-1 ${font}`}>Repayment</div>
            <span className={`bigger mb-1 ${font}`}>{monthlyPayment} </span>/month
            <div className='mb-1' />
            <div className={font}>Paying off P&I</div>
          </div>
        </div>
        {(isDesktop || !collapse) && (
          <div className={`loan-features ${displayFourCards}`}>
            <ul className={listGroup}>
              <li className={listGroupItem}><span className='medium'>Good for:</span></li>
              {renderListItems(goodFor)}
            </ul>
            <ul className={listGroup}>
              <li className={listGroupItem}><span className='medium'>Features:</span></li>
              {renderListItems(features)}
            </ul>
          </div>
        )}
        {!isDesktop && (
          <div
            id={`card-collapse-icon-${theme}`}
            className={`card-collapse-text ${theme} text-center ${collapseTextMargin}`}
            onClick={() => setCollapse(!collapse)}
          >
            {collapseText} {Arrow}
          </div>
        )}
        <div className='card-body bottom text-center'>
          <a className={`btn btn-lg ${applyNow} ${applyButton}`} href="/onboarding_funnel/new">Apply now</a>
          <span
            className={`btn btn-lg ${talkToExpert} mt-4`}
            onClick={() => { Calendly.showPopupWidget('https://calendly.com/yard_au'); return false; }}
          >
            Talk to an expert
          </span>
        </div>
      </div>
    </div>
  );
};

export default Card;